import React, { useEffect, useRef, useState } from "react";
import { CalendarClock, Loader2, ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import { getAttribution } from "../lib/attribution";
import LeadSourceField from "./LeadSourceField";
import { emptyLeadSource, leadSourceError, leadSourcePayload } from "../lib/leadSource";
import { useToast } from "../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { trackEvent, gaClientId } from "../lib/analytics";
import { consentChoice } from "./CookieConsent";

// "Book a Call" gate: the raw booking URL is never in the page source.
// A lightweight human check (honeypot + timing) is required before it's fetched.
// Calendly URLs open in an in-page popup overlay; other URLs open in a new tab.
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_JS = "https://assets.calendly.com/assets/external/widget.js";

export function loadCalendly() {
  return new Promise((resolve, reject) => {
    if (window.Calendly) return resolve(window.Calendly);
    if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_CSS;
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${CALENDLY_JS}"]`)) {
      const script = document.createElement("script");
      script.src = CALENDLY_JS;
      script.async = true;
      document.body.appendChild(script);
    }
    const started = Date.now();
    const iv = setInterval(() => {
      if (window.Calendly) { clearInterval(iv); resolve(window.Calendly); }
      else if (Date.now() - started > 8000) { clearInterval(iv); reject(new Error("Calendly load timeout")); }
    }, 80);
  });
}

export default function BookCallButton({ className = "", label = "Book a Call", testId = "book-call-btn" }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [leadSource, setLeadSource] = useState(emptyLeadSource);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceError, setSourceError] = useState("");
  const openedAt = useRef(0);
  const intentId = useRef("");

  // Calendly tells the page when a booking actually completes. Until it does,
  // the answer is only an intent — the report never claims a call that wasn't made.
  useEffect(() => {
    const onMessage = (e) => {
      // Calendly's own frames only. Any page can post a message into a tab, so
      // an origin check is what stops a booking being faked from elsewhere.
      let host = "";
      try { host = new URL(e.origin).hostname; } catch { return; }
      if (host !== "calendly.com" && !host.endsWith(".calendly.com")) return;
      if (e.data?.event !== "calendly.event_scheduled" || !intentId.current) return;
      publicApi.bookingConfirm({
        id: intentId.current,
        reference: e.data?.payload?.event?.uri || e.data?.payload?.invitee?.uri || "scheduled",
        // The identifier both routes share: one person's booking rather than one
        // calendar slot, so a group event stays several bookings.
        invitee_uri: e.data?.payload?.invitee?.uri || "",
      });
      intentId.current = "";
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const start = () => { openedAt.current = Date.now(); setWebsite(""); setSourceError(""); setOpen(true); };

  const proceed = async () => {
    const msg = leadSourceError(leadSource, sourceOptions, sourceOptions.length > 0);
    setSourceError(msg);
    if (msg) {
      document.querySelector('[data-testid="booking-lead-source-select"]')?.focus();
      return;
    }
    setBusy(true);
    try {
      const res = await publicApi.bookingLink({ website, elapsed_ms: Date.now() - openedAt.current });
      let url = res.url;
      // Recorded before the calendar opens, so the answer is kept even if they
      // close it again. Prefilled into Calendly's first custom question so it
      // sits on the booking itself next to their name.
      const intent = await publicApi.bookingIntent({
        lead_source: leadSourcePayload(leadSource),
        attribution: getAttribution(),
        page_url: window.location.pathname,
        // Lets a booking confirmed by Calendly's webhook later be reported to
        // GA4 against the same visitor rather than as an unknown one.
        ga_client_id: gaClientId(),
        // Captured now because the fallback runs long after the visitor has gone
        // and cannot ask then. Anything but a clear yes counts as no.
        analytics_consent: consentChoice(),
      });
      intentId.current = intent?.id || "";
      // Marks this as a booking the server will confirm, so the browser doesn't
      // report the same booking a second time.
      window.__cassBookingIntent = intentId.current || "";
      if (intent?.calendly_answer) {
        url += `${url.includes("?") ? "&" : "?"}${intent.calendly_param || "a1"}=${encodeURIComponent(intent.calendly_answer)}`;
      }
      // Secondary engagement event only — a genuine booking is reported by the
      // Calendly event_scheduled listener in lib/analytics.js.
      trackEvent("booking_link_click", { page_path: window.location.pathname });
      if (/calendly\.com/i.test(url)) {
        const Calendly = await loadCalendly();
        setOpen(false);
        Calendly.initPopupWidget({ url });
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
        setOpen(false);
      }
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many requests — please try again in a moment."
        : "Sorry, we couldn't open the booking page. Please try again or call us.";
      toast({ title: "Couldn't open booking", description: msg, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={start} data-testid={testId} className={className}>
        <CalendarClock size={16} /> {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="book-call-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy text-2xl">Book a call with Paul</DialogTitle>
            <DialogDescription className="text-slate-500">
              Pick a time that suits you and we'll talk through your business — no pressure, no obligation. You'll be taken to our secure online booking calendar.
            </DialogDescription>
          </DialogHeader>
          {/* Honeypot — hidden from humans */}
          <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
            <label>Do not fill this field<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
          </div>
          <LeadSourceField
            className="mt-2"
            value={leadSource}
            onChange={(v) => { setLeadSource(v); setSourceError(""); }}
            onOptions={setSourceOptions}
            required
            error={sourceError}
            testId="booking-lead-source"
          />
          <Button onClick={proceed} disabled={busy} data-testid="book-call-continue" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6 mt-2">
            {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Opening calendar…</> : <>Continue to booking <ArrowRight size={16} className="ml-2" /></>}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
